
import React, { useState } from 'react';
import { usePersonaContext } from '@/contexts/PersonaContext';
import { AppNavbar } from '@/components/layout/AppNavbar';
import { BenchmarkComparison } from '@/components/brand/BenchmarkComparison';
import { ChemicalSafetyBenchmark } from '@/components/brand/ChemicalSafetyBenchmark';
import { BenchmarkTable } from '@/components/brand/BenchmarkTable';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { ArrowLeft, TrendingUp, Shield, BarChart3, Table } from 'lucide-react';
import { Link } from 'react-router-dom';

const BrandBenchmarks = () => {
  const { currentPersona } = usePersonaContext();
  const [viewMode, setViewMode] = useState<'charts' | 'table'>('charts');
  
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <AppNavbar />
      
      <main className="flex-1">
        <ScrollArea className="h-[calc(100vh-4rem)]">
          <div className="container py-8 space-y-8">
            {/* Header */}
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Link to="/brand-user">
                  <Button variant="ghost" size="sm">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Dashboard
                  </Button>
                </Link>
              </div>
              
              <div className="space-y-2">
                <h1 className="text-3xl font-bold">Industry Benchmarks</h1>
                <p className="text-muted-foreground text-lg">
                  Compare your sustainability performance against industry peers and top performers
                </p>
              </div>

              {/* View Toggle */}
              <div className="flex items-center gap-2">
                <Button
                  variant={viewMode === 'charts' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('charts')}
                  className="flex items-center gap-2"
                >
                  <BarChart3 className="h-4 w-4" />
                  Option 1: Visual Charts
                </Button>
                <Button
                  variant={viewMode === 'table' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('table')}
                  className="flex items-center gap-2"
                >
                  <Table className="h-4 w-4" />
                  Option 2: Detailed Tables
                </Button>
              </div>
            </div>

            {viewMode === 'charts' ? (
              <>
                {/* KPI Benchmarks Section */}
                <div className="space-y-6">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-6 w-6 text-primary" />
                    <h2 className="text-2xl font-semibold">Key Performance Indicators</h2>
                  </div>
                  <p className="text-muted-foreground">
                    Your company's performance compared to similar-sized organizations in the industry
                  </p>
                  <BenchmarkComparison />
                </div>

                <Separator className="my-8" />

                {/* Chemical Safety Benchmarks Section */}
                <div className="space-y-6">
                  <div className="flex items-center gap-2">
                    <Shield className="h-6 w-6 text-primary" />
                    <h2 className="text-2xl font-semibold">Chemical Safety Scorecard Benchmarks</h2>
                  </div>
                  <p className="text-muted-foreground">
                    Safety endpoint performance comparison across your product portfolio
                  </p>
                  <ChemicalSafetyBenchmark />
                </div>
              </>
            ) : (
              <>
                {/* Table View */}
                <div className="space-y-6">
                  <div className="flex items-center gap-2">
                    <Table className="h-6 w-6 text-primary" />
                    <h2 className="text-2xl font-semibold">Detailed Benchmark Analysis</h2>
                  </div>
                  <p className="text-muted-foreground">
                    Comprehensive comparison with peer ranges and performance indicators
                  </p>
                  <BenchmarkTable />
                </div>
              </>
            )}

            {/* Footer space */}
            <div className="h-8" />
          </div>
        </ScrollArea>
      </main>
    </div>
  );
};

export default BrandBenchmarks;
